import React from 'react';
import '../assets/Reports.css';
import Card from './Card';
import TimeSeriesChart from './TimeSeriesChart';
import { LoadingSkeleton } from './LoadingSkeleton';
import { formatLocalDateTime } from '../utils/formatDateTime';
import usePagination from '../utils/usePagination';

const DIRECTION_CONFIG = {
  uplink: {
    label: 'Uplink',
    shortLabel: 'UL',
    seriesLabel: 'Uplink average peak rate',
    color: '#4472c4',
  },
  downlink: {
    label: 'Downlink',
    shortLabel: 'DL',
    seriesLabel: 'Downlink average peak rate',
    color: '#2e9d62',
  },
};

const formatRate = (value) => (value === null || value === undefined ? '—' : `${value.toFixed(1)} Mbps`);
const formatCount = (value) => (value ? value.toLocaleString() : '—');
const formatOutlierTimestamp = (value) => formatLocalDateTime(value, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZoneName: 'short',
});

const COMPACT_OUTLIER_ROWS = 5;
const EXPANDED_OUTLIER_ROWS = 10;

function normalizeOutliers(outliers) {
  return (Array.isArray(outliers) ? outliers : []).map((outlier, index) => ({
    id: `${outlier?.deviceId || outlier?.systemId || 'unknown'}-${outlier?.timestamp || 'unknown'}-${index}`,
    systemId: outlier?.deviceId || outlier?.systemId || '',
    peakRateMbps: Number.isFinite(Number(outlier?.peakRateMbps)) ? Number(outlier.peakRateMbps) : null,
    timestamp: outlier?.timestamp || '',
  }));
}

function compareOutliers(left, right, column) {
  if (column === 'peakRateMbps') return (left.peakRateMbps ?? -Infinity) - (right.peakRateMbps ?? -Infinity);
  if (column === 'timestamp') return new Date(left.timestamp || 0).getTime() - new Date(right.timestamp || 0).getTime();
  return left.systemId.localeCompare(right.systemId, undefined, { numeric: true, sensitivity: 'base' });
}

function SortableOutlierHeader({ column, label, activeColumn, direction, onSort }) {
  const indicator = column !== activeColumn ? '↕' : direction === 'asc' ? '↑' : '↓';
  return (
    <th scope="col">
      <button type="button" className="throughput-outlier-sort-button" onClick={() => onSort(column)}>
        <span>{label}</span><span aria-hidden="true">{indicator}</span>
      </button>
    </th>
  );
}

function ExpandIcon() {
  return (
    <svg className="throughput-expand-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 3H3v5M3 3l6 6M16 3h5v5M21 3l-6 6M8 21H3v-5M3 21l6-6M16 21h5v-5M21 21l-6-6" />
    </svg>
  );
}

function OutlierTableContent({ rows, onDeviceClick, rowsPerPage, onExpand, expanded = false }) {
  const [sortColumn, setSortColumn] = React.useState('timestamp');
  const [sortDirection, setSortDirection] = React.useState('desc');
  const [currentPage, setCurrentPage] = React.useState(1);
  const sortedRows = React.useMemo(() => [...rows].sort((left, right) => {
    const result = compareOutliers(left, right, sortColumn);
    return sortDirection === 'asc' ? result : -result;
  }), [rows, sortColumn, sortDirection]);

  React.useEffect(() => setCurrentPage(1), [rows, rowsPerPage, sortColumn, sortDirection]);

  const { pageItems, totalPages, renderPageNumbers } = usePagination(sortedRows, currentPage, setCurrentPage, rowsPerPage);
  const handleSort = (column) => {
    if (column === sortColumn) setSortDirection((value) => value === 'asc' ? 'desc' : 'asc');
    else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  return (
    <div className={`throughput-outlier-table-shell${expanded ? ' throughput-outlier-table-shell-expanded' : ''}`}>
      <div className="throughput-outlier-table-heading">
        <div>
          <strong>Reported outlier samples</strong>
          <span>{expanded ? 'Expanded sample view · up to 10 rows per page' : 'Sort samples or open an expanded view'}</span>
        </div>
        {!expanded && (
          <button type="button" className="throughput-outlier-expand-button" onClick={onExpand} aria-label="Expand outlier table" title="Expand table">
            <ExpandIcon />
          </button>
        )}
      </div>
      <div className="throughput-outlier-table-scroll">
        <table className="throughput-outlier-table">
          <colgroup><col className="throughput-outlier-system-id-column" /><col /><col /><col /></colgroup>
          <thead><tr>
            <SortableOutlierHeader column="systemId" label="System ID" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
            <SortableOutlierHeader column="peakRateMbps" label="Peak rate" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
            <SortableOutlierHeader column="timestamp" label="Local timestamp" activeColumn={sortColumn} direction={sortDirection} onSort={handleSort} />
            <th scope="col"><span className="sr-only">Action</span></th>
          </tr></thead>
          <tbody>{pageItems.map((outlier) => (
            <tr key={outlier.id}>
              <td className="throughput-outlier-system-id" title={outlier.systemId || undefined}>{outlier.systemId || '—'}</td>
              <td>{formatRate(outlier.peakRateMbps)}</td>
              <td><time dateTime={outlier.timestamp || undefined}>{formatOutlierTimestamp(outlier.timestamp) || '—'}</time></td>
              <td className="throughput-outlier-action">{outlier.systemId && onDeviceClick ? (
                <button type="button" className="throughput-investigate-link" onClick={() => onDeviceClick({ systemId: outlier.systemId, referenceTimestamp: outlier.timestamp })}>Investigate</button>
              ) : '—'}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="throughput-outlier-pagination">
        <span>{sortedRows.length} sample{sortedRows.length === 1 ? '' : 's'} · {totalPages > 1 ? `Page ${currentPage} of ${totalPages}` : 'Page 1'}</span>
        <div className="throughput-outlier-page-buttons">{renderPageNumbers()}</div>
      </div>
    </div>
  );
}

function OutlierTable({ outliers, onDeviceClick }) {
  const [expanded, setExpanded] = React.useState(false);
  const rows = React.useMemo(() => normalizeOutliers(outliers), [outliers]);
  React.useEffect(() => {
    if (!expanded) return undefined;
    const closeOnEscape = (event) => { if (event.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [expanded]);

  if (rows.length === 0) return null;

  return (
    <div className="throughput-outlier-table-wrap">
      <OutlierTableContent rows={rows} onDeviceClick={onDeviceClick} rowsPerPage={COMPACT_OUTLIER_ROWS} onExpand={() => setExpanded(true)} />
      {expanded && (
        <div className="throughput-outlier-modal-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setExpanded(false);
        }}>
          <div className="throughput-outlier-modal" role="dialog" aria-modal="true" aria-label="Reported throughput outlier samples">
            <div className="throughput-outlier-modal-header">
              <div><strong>Throughput outlier samples</strong><span>Inspect reported samples and start a device investigation.</span></div>
              <button type="button" onClick={() => setExpanded(false)} aria-label="Close expanded table">×</button>
            </div>
            <div className="throughput-outlier-modal-body"><OutlierTableContent rows={rows} onDeviceClick={onDeviceClick} rowsPerPage={EXPANDED_OUTLIER_ROWS} expanded /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function DirectionStats({ direction, stats, timeRange, onDeviceClick }) {
  const config = DIRECTION_CONFIG[direction];
  const [chartExpanded, setChartExpanded] = React.useState(false);
  const hasData = Boolean(stats && stats.sampleCount > 0);
  const series = hasData
      ? [{
        name: config.seriesLabel,
        color: config.color,
        data: stats.trendData,
      }]
    : [];

  React.useEffect(() => {
    if (!chartExpanded) return undefined;
    const closeOnEscape = (event) => { if (event.key === 'Escape') setChartExpanded(false); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [chartExpanded]);

  return (
    <div className="throughput-direction-panel" data-testid={`throughput-${direction}`}>
      <div className="throughput-direction-heading">
        <span className="throughput-direction-dot" style={{ backgroundColor: config.color }} />
        <div>
          <h3>{config.label}</h3>
          <span>{hasData ? `${formatCount(stats.sampleCount)} samples` : 'No samples in selected range'}</span>
        </div>
        <div className="throughput-average-value">
          <span className="throughput-average-label">Device and Time Averaged Peak Rate</span>
          <strong>{hasData ? formatRate(stats.avgRateMbps) : '—'}</strong>
        </div>
      </div>

      <div className="throughput-stat-grid">
        <div><span>P50</span><strong>{formatRate(stats?.p50RateMbps)}</strong></div>
        <div><span>P95</span><strong>{formatRate(stats?.p95RateMbps)}</strong></div>
        <div><span>P99</span><strong>{formatRate(stats?.p99RateMbps)}</strong></div>
        <div><span>Min</span><strong>{formatRate(stats?.minRateMbps)}</strong></div>
        <div><span>Max</span><strong>{formatRate(stats?.maxRateMbps)}</strong></div>
        <div><span>Std dev</span><strong>{formatRate(stats?.stdDevMbps)}</strong></div>
      </div>

      {stats?.thresholdMbps != null && (
        <div className="throughput-outlier-note">
          <strong>{stats.qualifyingSamplePercentage?.toFixed(1) ?? '0.0'}% at or above {formatRate(stats.thresholdMbps)}</strong>
          <span>{formatCount(stats.qualifyingSampleCount)} of {formatCount(stats.sampleCount)} samples</span>
        </div>
      )}

      <div className="throughput-chart-shell">
        <button
          type="button"
          className="throughput-chart-expand-button"
          onClick={() => setChartExpanded(true)}
          disabled={!hasData}
          aria-label={`Expand ${config.label.toLowerCase()} average peak rate chart`}
          title={`Expand ${config.label.toLowerCase()} chart`}
        >
          <ExpandIcon />
        </button>
        <TimeSeriesChart
          title={`${config.label} average peak rate`}
          series={series}
          yAxisLabel="Mbps"
          timeRange={timeRange}
          timeZone="local"
          chartHeight="220px"
        />
      </div>

      {hasData && stats.outliers.length > 0 && (
        <div className="throughput-outlier-note" title="A reported outlier is a peak-rate sample more than two standard deviations above or below its hourly average. Aggregate and device-tag views return the most extreme high and low cases.">
          <strong>{stats.outliers.length.toLocaleString()} reported outlier sample{stats.outliers.length === 1 ? '' : 's'}</strong>
          <span>More than 2σ from the hourly average</span>
        </div>
      )}
      <OutlierTable outliers={stats?.outliers} onDeviceClick={onDeviceClick} />
      {chartExpanded && (
        <div className="throughput-chart-modal-overlay" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setChartExpanded(false);
        }}>
          <div className="throughput-chart-modal" role="dialog" aria-modal="true" aria-label={`${config.label} average peak rate chart`}>
            <div className="throughput-chart-modal-header">
              <div>
                <strong>{config.label} average peak rate</strong>
                <span>Device- and time-averaged peak rate · local time</span>
              </div>
              <button type="button" onClick={() => setChartExpanded(false)} aria-label={`Close ${config.label.toLowerCase()} chart`}>×</button>
            </div>
            <div className="throughput-chart-modal-body">
              <TimeSeriesChart
                title={`${config.label} average peak rate`}
                series={series}
                yAxisLabel="Mbps"
                timeRange={timeRange}
                timeZone="local"
                showTitle={false}
                chartHeight="600px"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ThroughputLoading() {
  return (
    <div className="throughput-direction-panel">
      <LoadingSkeleton height="36px" />
      <LoadingSkeleton height="64px" />
      <LoadingSkeleton height="220px" />
    </div>
  );
}

export default function ThroughputStatsSection({ data, loading, error, timeRange = '7d', analyticsStyle = false, suppressAnalyticsHeader = false, onDeviceClick, onThresholdApply }) {
  const [thresholdInput, setThresholdInput] = React.useState('');
  const applyThreshold = () => {
    const threshold = Number(thresholdInput);
    if (Number.isFinite(threshold) && threshold > 0 && onThresholdApply) onThresholdApply(threshold);
  };
  const content = (
    <>
      {onThresholdApply && (
        <div className="throughput-coverage-control">
          <label htmlFor="throughput-coverage-threshold">Coverage threshold (Mbps)</label>
          <input id="throughput-coverage-threshold" type="number" min="0.1" step="0.1" value={thresholdInput} onChange={(event) => setThresholdInput(event.target.value)} />
          <button type="button" onClick={applyThreshold} disabled={!Number.isFinite(Number(thresholdInput)) || Number(thresholdInput) <= 0}>Apply</button>
        </div>
      )}
      {loading ? (
        <div className="throughput-direction-grid">
          <ThroughputLoading />
          <ThroughputLoading />
        </div>
      ) : error ? (
        <div className="throughput-state throughput-error">
          <strong>Throughput data unavailable</strong>
          <span>{error.message || 'Unable to load throughput statistics.'}</span>
        </div>
      ) : (
        <div className="throughput-direction-grid">
          <DirectionStats direction="uplink" stats={data?.uplink} timeRange={timeRange} onDeviceClick={onDeviceClick} />
          <DirectionStats direction="downlink" stats={data?.downlink} timeRange={timeRange} onDeviceClick={onDeviceClick} />
        </div>
      )}
    </>
  );

  if (analyticsStyle) {
    if (suppressAnalyticsHeader) return content;

    return (
      <>
        <div className="sec-hdr">
          <div className="sec-icon blue">📶</div>
          <h2>Throughput</h2>
          <div className="line" />
        </div>
        {content}
      </>
    );
  }

  return (
    <Card title="Throughput" collapsible>
      {content}
    </Card>
  );
}
